import type {
  NotificationPreferenceConfigDto,
  NotificationPreferenceDto,
  NotificationPreferencesConfigDto,
} from '@personnel/application/dtos';
import { NOTIFICATION_CHANNEL_ORDER } from '@personnel/domain/types';
import type { NotificationChannel } from '@personnel/domain/types';
import type { NotificationPreferencesFormValue } from '@personnel/presentation/types';

/** How one checkbox of the matrix is painted. */
export type NotificationChannelState = {
  isChecked: boolean;
  isLocked: boolean;
};

/**
 * How the configuration wants one checkbox painted.
 *
 * A channel the group does not offer is painted off and locked, whatever the user has stored for
 * it: an offer that was withdrawn is not a preference the screen can honour. A mandatory channel is
 * painted on and locked. Everything else is the user's to decide.
 */
export const resolveChannelState = (
  entry: NotificationPreferenceConfigDto,
  channel: NotificationChannel,
  isCheckedByUser: boolean,
): NotificationChannelState => {
  if (!entry.availableChannels.includes(channel)) {
    return { isChecked: false, isLocked: true };
  }

  if (entry.mandatoryChannels.includes(channel)) {
    return { isChecked: true, isLocked: true };
  }

  return { isChecked: isCheckedByUser, isLocked: false };
};

/** Whether a group belongs to the always-on block, which is the one the user cannot switch off. */
export const isAlwaysOnGroup = (entry: NotificationPreferenceConfigDto): boolean => entry.mandatoryChannels.length > 0;

/** The two blocks the screen paints, each keeping the order the configuration arrived in. */
export const splitNotificationGroups = (
  config: NotificationPreferencesConfigDto,
): { editable: NotificationPreferencesConfigDto; alwaysOn: NotificationPreferencesConfigDto } => ({
  editable: config.filter((entry) => !isAlwaysOnGroup(entry)),
  alwaysOn: config.filter(isAlwaysOnGroup),
});

/**
 * The form value for the matrix, from the configuration and what the user has stored.
 *
 * The configuration decides which rows exist; the stored preferences only decide the rows that are
 * neither mandatory nor withdrawn. A group the read did not mention is a group with nothing on.
 */
export const buildNotificationPreferencesFormValue = (
  config: NotificationPreferencesConfigDto,
  saved: ReadonlyArray<NotificationPreferenceDto> = [],
): NotificationPreferencesFormValue =>
  config.reduce<NotificationPreferencesFormValue>((formValue, entry) => {
    const savedChannels = saved.find((item) => item.notificationGroup === entry.notificationGroup)?.channels ?? [];

    formValue[entry.notificationGroup] = NOTIFICATION_CHANNEL_ORDER.reduce(
      (channels, channel) => ({
        ...channels,
        [channel]: resolveChannelState(entry, channel, savedChannels.includes(channel)).isChecked,
      }),
      {} as Record<NotificationChannel, boolean>,
    );

    return formValue;
  }, {});

/**
 * The body of the write, from the configuration and the live form value.
 *
 * Every group the screen painted travels, an empty channel list included: on this endpoint an empty
 * list switches the group off while an absent group is left untouched. Only what was painted
 * travels, so a group the application does not know cannot be switched off by a save that never
 * showed it.
 */
export const notificationPreferencesFormValueToDto = (
  config: NotificationPreferencesConfigDto,
  formValue: NotificationPreferencesFormValue,
): ReadonlyArray<NotificationPreferenceDto> =>
  config.map((entry) => {
    const channels = formValue[entry.notificationGroup];

    return {
      notificationGroup: entry.notificationGroup,
      channels: NOTIFICATION_CHANNEL_ORDER.filter(
        (channel) => resolveChannelState(entry, channel, !!channels?.[channel]).isChecked,
      ),
    };
  });
